import { useState, useEffect, useRef, useCallback } from 'react';
import mqtt from 'mqtt';
import {
  MQTT_CONFIG,
  MESSAGE_TIMEOUT,
  CONNECTION_CHECK_INTERVAL,
  HISTORY_LIMIT,
} from '../config';
import { apiGet } from '../utils/api';
import { notifyRoomSeen } from '../utils/api';

/**
 * Custom hook for managing MQTT connection and room data
 * @param {number} refreshInterval - Refresh interval in seconds
 * @returns {Object} - MQTT state and data
 */
export const useMQTT = (refreshInterval = 1) => {
  // Rooms data - stores data for all rooms
  const [rooms, setRooms] = useState({});

  // Room-specific data for dashboard view
  const [roomHistory, setRoomHistory] = useState({});
  const [roomPreviousData, setRoomPreviousData] = useState({});
  const [roomMessageCount, setRoomMessageCount] = useState({});

  // Displayed data - only updates based on refresh interval
  const [displayedRooms, setDisplayedRooms] = useState({});

  // Current time state - updates every second
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Refs
  const lastMessageTimeRef = useRef({});
  const connectionCheckIntervalRef = useRef(null);
  const latestDataBufferRef = useRef({});
  const lastSyncRef = useRef({}); // throttle notifications to API
  const roomsFetchIntervalRef = useRef(null);

  // Update current time every second
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timeInterval);
  }, []);

  // Update displayed data based on refresh interval
  useEffect(() => {
    const intervalMs = refreshInterval * 1000;
    console.log(`🔄 Refresh interval changed to ${intervalMs}ms (${refreshInterval}s)`);

    const updateDisplayedData = () => {
      if (Object.keys(latestDataBufferRef.current).length > 0) {
        setDisplayedRooms(prev => {
          const newData = { ...prev };
          Object.keys(latestDataBufferRef.current).forEach(key => {
            newData[key] = {
              ...latestDataBufferRef.current[key],
              lastUpdate: Date.now()
            };
          });
          return newData;
        });
      }
    };

    // Update immediately when interval changes
    updateDisplayedData();

    const dataInterval = setInterval(updateDisplayedData, intervalMs);

    return () => clearInterval(dataInterval);
  }, [refreshInterval]);

  // MQTT Connection
  useEffect(() => {
    console.log('🔄 Initializing MQTT connection...');

    const client = mqtt.connect(MQTT_CONFIG.broker, {
      username: MQTT_CONFIG.username,
      password: MQTT_CONFIG.password,
      clientId: `${MQTT_CONFIG.clientIdPrefix}-${Date.now()}`,
      clean: true,
      reconnectPeriod: MQTT_CONFIG.reconnectPeriod,
      connectTimeout: MQTT_CONFIG.connectTimeout,
    });

    client.on('connect', () => {
      console.log('✅ Connected to MQTT broker');
      client.subscribe(MQTT_CONFIG.topic, (err) => {
        if (err) {
          console.error('❌ Subscription error:', err);
        } else {
          console.log(`📡 Subscribed to topic: ${MQTT_CONFIG.topic}`);
        }
      });
    });

    client.on('error', (err) => {
      console.error('❌ MQTT Error:', err);
    });

    client.on('message', (topic, message) => {
      try {
        const data = JSON.parse(message.toString());
        const roomCode = data.code;
        const now = Date.now();

        if (!roomCode) {
          console.error('❌ Message without room code');
          return;
        }

        // Update last message time for this room
        lastMessageTimeRef.current[roomCode] = now;

        // Notify backend (throttled to once per 60s per room)
        const lastSync = lastSyncRef.current[roomCode] || 0;
        if (now - lastSync > 60000) {
          lastSyncRef.current[roomCode] = now;
          notifyRoomSeen({ code: roomCode, timestamp: data.timestamp || new Date().toISOString() });
        }

        // Store in buffer for next display update
        latestDataBufferRef.current[roomCode] = {
          code: roomCode,
          hasData: true,
          data: data,
          lastUpdate: now
        };

        // Update rooms state (for room list)
        setRooms(prev => ({
          ...prev,
          [roomCode]: {
            code: roomCode,
            hasData: true,
            data: data,
            lastUpdate: now
          }
        }));

        // Update room history
        setRoomHistory(prev => {
          const currentHistory = prev[roomCode] || [];

          // Parse timestamp safely - use current time as fallback
          let timeString;
          const timestamp = data.timestamp;

          if (timestamp) {
            const date = new Date(timestamp);
            // Check if date is valid
            if (!isNaN(date.getTime())) {
              timeString = date.toLocaleTimeString('es-ES', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              });
            } else {
              // Invalid date format, use current time
              timeString = new Date().toLocaleTimeString('es-ES', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              });
            }
          } else {
            // No timestamp provided, use current time
            timeString = new Date().toLocaleTimeString('es-ES', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            });
          }

          const newEntry = {
            ...data,
            time: timeString
          };
          return {
            ...prev,
            [roomCode]: [...currentHistory, newEntry].slice(-HISTORY_LIMIT)
          };
        });

        // Update previous data
        setRoomPreviousData(prev => ({
          ...prev,
          [roomCode]: prev[`${roomCode}_current`] || null,
          [`${roomCode}_current`]: data
        }));

        // Update message count
        setRoomMessageCount(prev => ({
          ...prev,
          [roomCode]: (prev[roomCode] || 0) + 1
        }));

      } catch (err) {
        console.error('❌ Parse error:', err);
      }
    });

    // Periodic connection check
    connectionCheckIntervalRef.current = setInterval(() => {
      const now = Date.now();

      setRooms(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(roomCode => {
          const lastTime = lastMessageTimeRef.current[roomCode];
          if (lastTime && (now - lastTime) > MESSAGE_TIMEOUT) {
            updated[roomCode] = {
              ...updated[roomCode],
              hasData: false
            };
          }
        });
        return updated;
      });
    }, CONNECTION_CHECK_INTERVAL);

    return () => {
      console.log('🛑 Cleaning up MQTT connection...');
      if (connectionCheckIntervalRef.current) {
        clearInterval(connectionCheckIntervalRef.current);
      }
      if (client) {
        client.end(true);
      }
    };
  }, []);

  // Fetch rooms from API to include registered rooms without MQTT
  useEffect(() => {
    let cancelled = false;

    const mergeRoomsFromApi = async () => {
      try {
        const data = await apiGet('/rooms');
        const list = data?.rooms || [];
        if (cancelled) return;
        setRooms(prev => {
          const updated = { ...prev };
          list.forEach((r) => {
            const code = r.code;
            const prevRoom = updated[code];
            if (prevRoom && prevRoom.hasData) {
              // keep live MQTT data/state
              return;
            }
            updated[code] = {
              code,
              hasData: !!r.hasData,
              data: prevRoom?.data || null,
              lastUpdate: r.lastUpdate || prevRoom?.lastUpdate || null,
            };
          });
          return updated;
        });
      } catch (e) {
        // ignore transient API errors
        // console.warn('rooms fetch failed:', e?.message || e);
      }
    };

    // initial fetch and interval
    mergeRoomsFromApi();
    roomsFetchIntervalRef.current = setInterval(mergeRoomsFromApi, 15000);

    return () => {
      cancelled = true;
      if (roomsFetchIntervalRef.current) clearInterval(roomsFetchIntervalRef.current);
    };
  }, []);

  // Get room data helper
  const getRoomData = useCallback((roomCode) => {
    if (!roomCode) return null;

    const currentRoomData = displayedRooms[roomCode] || rooms[roomCode];

    return {
      roomData: currentRoomData,
      latestData: currentRoomData?.data || null,
      hasReceivedData: currentRoomData?.hasData || false,
      history: roomHistory[roomCode] || [],
      previousData: roomPreviousData[roomCode] || null,
      messageCount: roomMessageCount[roomCode] || 0,
    };
  }, [displayedRooms, rooms, roomHistory, roomPreviousData, roomMessageCount]);

  return {
    rooms,
    displayedRooms,
    currentTime,
    getRoomData,
  };
};
